import { ReactNode } from 'react';
import { Button } from '@/components/ui/button';
import { ModalShell } from '@/components/ModalShell';
import { useT } from '@/i18n';
import { cn } from '@/lib/utils';

export type ConfirmTone = 'neutral' | 'warning' | 'danger';

interface ConfirmDialogProps {
  open: boolean;
  title: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  tone?: ConfirmTone;
  loading?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

const TONE_STYLES: Record<ConfirmTone, { headerBar: string; accent: string; titleText: string; confirmVariant: 'primary' | 'warning' | 'danger' }> = {
  neutral: {
    headerBar: 'bg-nav-tealTint border-[#c9e2e2]',
    accent: 'text-nav-tealDark',
    titleText: 'text-nav-navy',
    confirmVariant: 'primary',
  },
  warning: {
    headerBar: 'bg-nav-amberTint border-nav-amber/40',
    accent: 'text-nav-amberText',
    titleText: 'text-nav-navy',
    confirmVariant: 'warning',
  },
  danger: {
    headerBar: 'bg-nav-redTint border-[#e3b5ae]',
    accent: 'text-nav-red',
    titleText: 'text-nav-navy',
    confirmVariant: 'danger',
  },
};

export function ConfirmDialog({
  open,
  title,
  message,
  confirmLabel,
  cancelLabel,
  tone = 'neutral',
  loading = false,
  onConfirm,
  onCancel,
}: ConfirmDialogProps): ReactNode {
  const t = useT();
  const styles = TONE_STYLES[tone];
  const finalConfirm = confirmLabel ?? t('bookings.confirm.confirmCta');
  const finalCancel = cancelLabel ?? t('bookings.confirm.cancelCta');

  return (
    <ModalShell
      open={open}
      onClose={onCancel}
      ariaLabelledBy="confirm-dialog-title"
      ariaDescribedBy="confirm-dialog-message"
    >
      <header
        className={cn(
          'sticky top-0 px-6 py-4 border-b rounded-t-lg z-10',
          styles.headerBar,
        )}
      >
        <h2
          id="confirm-dialog-title"
          className={cn('font-head text-[15px] font-bold tracking-tight', styles.titleText)}
        >
          {title}
        </h2>
      </header>

      <div className="modal-scroll flex-1 overflow-y-auto px-6 py-5">
        <p
          id="confirm-dialog-message"
          className={cn('text-sm leading-relaxed', styles.accent)}
        >
          {message}
        </p>
      </div>

      <footer className="sticky bottom-0 bg-white border-t border-nav-card px-6 py-3 flex justify-end gap-2 rounded-b-lg">
        <Button
          size="sm"
          variant="secondary"
          onClick={onCancel}
          disabled={loading}
        >
          {finalCancel}
        </Button>
        <Button
          size="sm"
          variant={styles.confirmVariant}
          onClick={onConfirm}
          disabled={loading}
        >
          {finalConfirm}
        </Button>
      </footer>
    </ModalShell>
  );
}
